import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetMarketShopRestaurantOrders } from '@/api/market-shop-restaurant/get-market-shop-restaurant-orders'
import { Alert } from '@/components/ui/alert'
import { Typography } from '@/components/ui/typography'
import { useAutoPageReload, useScreenOrientation, useSSE } from '@/hooks'
import { MarketShopOrderModel } from '@/models'
import { OrderCardOverlay } from '@/pages/market-shop-restaurant/orders-column/order-card-overlay/order-card-overlay'
import { getApiErrorMessage } from '@/utils'

import { Header } from './header'
import {
  MARKET_SHOP_RESTAURANT_AUTO_RELOAD_KEY,
  newOrderAlertAudio,
  orderStatusColumnsOrder,
  removeNewOrderStatusTimeoutMS,
} from './market-shop-restaurant.constants'
import { useMarketShopRestaurantStore } from './market-shop-restaurant.store'
import * as S from './market-shop-restaurant.styled'
import { OrderCard, OrdersColumn } from './orders-column'

export const MarketShopRestaurantPage = () => {
  const { t } = useTranslation(['market-shop-restaurant'])
  const { isLandscape } = useScreenOrientation()
  const { orders, registerNewOrder, removeOrderFromNewSet, initializeOrders } =
    useMarketShopRestaurantStore()

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
    error: ordersError,
  } = useGetMarketShopRestaurantOrders()

  const restaurantSSE = useSSE(
    `${import.meta.env.VITE_API_URL}/sse?channels=new_restaurant_order_${ordersData?.marketShopId}&public=1`,
    {
      events: {
        new_order: (event) => {
          try {
            const { order } = JSON.parse(event.data) as { order: MarketShopOrderModel }

            registerNewOrder(order)

            // TODO: Make audio permission check to better handle cases where audio won't be played?
            newOrderAlertAudio.play().catch((e) => console.error('Audio play failed:', e))

            setTimeout(() => {
              removeOrderFromNewSet(order)
            }, removeNewOrderStatusTimeoutMS)
          } catch {
            /* empty */
          }
        },
      },
    },
  )

  useEffect(() => {
    if (!ordersData) return
    initializeOrders(ordersData.orders)

    let isCancelled = false
    let retryTimeout: ReturnType<typeof setTimeout>

    // This kiosk display needs to keep receiving live order notifications all day, so a
    // dropped/timed-out SSE connection (network blip, idle proxy timeout) must retry rather
    // than leave the page silently without new-order alerts until the 3am auto-reload.
    const connectWithRetry = () => {
      const scheduleRetry = () => {
        if (!isCancelled) {
          retryTimeout = setTimeout(connectWithRetry, 5000)
        }
      }
      restaurantSSE
        .connect()
        .then((es) => {
          es.addEventListener('error', () => {
            if (es.readyState === EventSource.CLOSED) scheduleRetry()
          })
        })
        .catch((error: unknown) => {
          console.error('Failed to connect to restaurant orders SSE, retrying:', error)
          scheduleRetry()
        })
    }
    connectWithRetry()

    return () => {
      isCancelled = true
      clearTimeout(retryTimeout)
    }
  }, [ordersData])

  useAutoPageReload({
    storageKey: MARKET_SHOP_RESTAURANT_AUTO_RELOAD_KEY,
  })

  if (!isLandscape) {
    return (
      <S.ScreenOrientationContainer>
        <S.RotateDeviceImg
          src="/images/screen-orientation.svg"
          alt={t('market-shop-restaurant:rotateDevice.title')}
        />
        <Typography as="h1" size="heading-huge" weight="semibold" color="black800">
          {t('market-shop-restaurant:rotateDevice.title')}
        </Typography>
        <Typography as="p" size="heading-medium" weight="medium" color="black500">
          {t('market-shop-restaurant:rotateDevice.subtitle')}
        </Typography>
      </S.ScreenOrientationContainer>
    )
  }

  return (
    <S.Root>
      <Header />
      <S.ContentContainer>
        {isOrdersError ? (
          <S.ErrorContainer>
            <Alert type="error" title={getApiErrorMessage(ordersError)} />
          </S.ErrorContainer>
        ) : (
          orderStatusColumnsOrder.map((status) => (
            <OrdersColumn
              key={status}
              status={status}
              count={orders[status].length}
              loading={isOrdersLoading}
            >
              {orders[status].map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </OrdersColumn>
          ))
        )}
      </S.ContentContainer>
      <OrderCardOverlay />
    </S.Root>
  )
}
